package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.QuietCauseDto
import co.maytes.standupbot.shared.dto.standuprun.RunLeaveDto
import co.maytes.standupbot.shared.leave.LeaveRowText
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.LocalDate

@Composable
fun ExcludedPanel(
    leave: List<RunLeaveDto>,
    postDate: LocalDate,
    quiet: List<QuietRunRules.Row> = emptyList(),
    isAdmin: Boolean = false,
    canRerun: Boolean = false,
    onOpenRoster: () -> Unit = {},
    onRerun: () -> Unit = {},
) {
    if (leave.isEmpty() && quiet.isEmpty()) return
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(Spacing.xs),
        ) {
            SpanText(
                text = if (quiet.isEmpty()) "Excluded from this run" else "Not in this run's standup",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary),
            )
            leave.forEach { row ->
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                    modifier = Modifier.fillMaxWidth().styleModifier { property("flex-wrap", "wrap") },
                ) {
                    SpanText(text = "🌴", modifier = Modifier.fontSize(Typography.bodySmall.size))
                    SpanText(
                        text = row.displayName ?: "Someone",
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .fontWeight(600)
                            .color(Palette.textPrimary),
                    )
                    SpanText(
                        text = LeaveRowText.describe(row, postDate),
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textSecondary),
                    )
                }
            }
            quiet.take(QuietRunRules.MAX_ROWS).forEach { row ->
                QuietRow(row, isAdmin, canRerun, onOpenRoster, onRerun)
            }
            val hidden = quiet.size - QuietRunRules.MAX_ROWS
            if (hidden > 0) {
                SpanText(
                    text = "+$hidden more",
                    modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
                )
            }
        }
    }
}

@Composable
private fun QuietRow(
    row: QuietRunRules.Row,
    isAdmin: Boolean,
    canRerun: Boolean,
    onOpenRoster: () -> Unit,
    onRerun: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        modifier = Modifier.fillMaxWidth().styleModifier { property("flex-wrap", "wrap") },
    ) {
        SpanText(
            text = row.name,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(600)
                .color(Palette.textPrimary),
        )
        Badge(label = row.label, tone = toneFor(row.cause))
        SpanText(
            text = row.sentence,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        val fix = row.fix
        if (isAdmin && fix != null && (fix != QuietRunRules.Fix.RERUN || canRerun)) {
            Button(
                label = QuietRunRules.fixLabel(fix),
                variant = ButtonVariant.Secondary,
                size = ButtonSize.Sm,
                onClick = { if (fix == QuietRunRules.Fix.RERUN) onRerun() else onOpenRoster() },
            )
        }
    }
}

private fun toneFor(cause: QuietCauseDto): BadgeTone =
    when (cause) {
        QuietCauseDto.NOT_LINKED, QuietCauseDto.PARTIALLY_LINKED -> BadgeTone.Warning
        QuietCauseDto.SOURCE_FAILED -> BadgeTone.Error
        else -> BadgeTone.Neutral
    }
